import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { AiOutlineClose } from 'react-icons/ai';
import apiCall from '../../../../api';
import { useAppDispatch, useAppSelector } from '../../../hooks/useRedux';
import { fetchBankIdVerificationStatus } from '../../../features/bankid/bankid.slice';
import { fetchPersonalData, updateBankData } from '../../../features/profile-settings/profile-settings.slice';
import { BankMethodSelector } from '../../bank/bank-method-selector.component';
import { BankOTPModal } from '../../BankOTPModal';
import { deriveBankMethodValue, buildManualBankPayload, isValidClearingNumber, isValidAccountNumber } from '../../../features/bank/bank.helpers';
import { BankMethodValue, BankFieldErrors } from '../../../features/bank/bank.types';
import { getCustomerDob } from '../../../utils/registration-validation';
import OnboardingIdentityVerification from './onboarding-identity-verification.component';
import OnboardingEmailVerification from './onboarding-email-verification.component';
import OnboardingStepper from './onboarding-stepper.component';
import ready_platform from '../../../../../public/assets/svg/ready_platform.svg';
import './onboarding-post-profile-steps.style.scss';
import '../../../scss/profile-settings.style.scss';

/**
 * Post-profile onboarding steps, rendered INLINE inside the persistent profile
 * modal (the parent shell) — no nested modals, so navigating never closes the
 * modal. Order: bank → email → verify → finish. Every step is SHOWN (never
 * auto-skipped): already-completed steps display a "done" summary panel with a
 * checkmark, so the user can Back/Next through the whole flow.
 *
 * The email step exists because Brite (and manual bank entry) never confirm
 * the user actually owns the email on file — Brite only uses it to prefill the
 * bank-connection session — so it's verified explicitly here via OTP, right
 * after the bank step, using the same generate-otp(type 4) → verify-new-v2
 * contract Profile Settings already uses for an unverified account.
 *
 * The bank step reads/writes bank info through the SAME source as Profile
 * Settings (profile-settings.slice's fetchPersonalData → /employee/get/{userId})
 * so isBriteIdExist/isBriteSessionAccount/customerSalaryInformation are read
 * from the one confirmed-correct shape, and uses the same Brite-first flow
 * (<BankMethodSelector> + <BankOTPModal>). Once a bank is on file it's shown
 * read-only here — no edit/remove — this step is a one-time setup flow, not
 * the bank management page.
 */
type Step = 'bank' | 'email' | 'verify' | 'finish';

interface OnboardingPostProfileStepsProps {
  isLoginWithBankId: boolean;
  onStartTour: () => void;
  onComplete: () => void;
  /** Go back to the profile step (parent hides the post-steps). */
  onBack: () => void;
  /** Deep-link: open directly at this step (from the dashboard card). */
  initialStep?: Step;
}

const DoneIcon: React.FC = () => (
  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="11" fill="#e9f8f1" stroke="#2bb673" strokeWidth="1.5" />
    <path d="M7 12.5l3.2 3.2L17 9" stroke="#2bb673" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const OnboardingPostProfileSteps: React.FC<OnboardingPostProfileStepsProps> = ({
  isLoginWithBankId,
  onStartTour,
  onComplete,
  onBack,
  initialStep,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isIdentityVerified = useAppSelector((s: any) => s?.bankidAuthFlow?.isIdentityVerified);
  const authUser = useAppSelector((s: any) => s?.auth?.user);
  // Same slice/endpoint as profile-settings.page.tsx — confirmed to carry
  // isBriteIdExist / isBriteSessionAccount / customerSalaryInformation correctly.
  const personalData = useAppSelector((s: any) => s?.profileSettings?.personalData);

  const [step, setStep] = useState<Step>(initialStep ?? 'bank');
  const [verified, setVerified] = useState(false);
  const [initializing, setInitializing] = useState(true);
  // Bank method state for BankMethodSelector (Brite / international / manual).
  const [bankValue, setBankValue] = useState<BankMethodValue>(() => deriveBankMethodValue(null));
  const [bankFieldErrors, setBankFieldErrors] = useState<BankFieldErrors>({});
  const [otpModalOpen, setOtpModalOpen] = useState<boolean>(false);
  const [otpField, setOtpField] = useState<string[]>(new Array(4).fill(''));
  const [otpCounter, setOtpCounter] = useState(300);
  const otpInputRefs = useRef<any[]>([]);

  // Swipe up/down hints — identical behaviour to the registration form: "down"
  // shows while more content is below, "up" only once scrolled, neither when it
  // all fits. Same thresholds and step (85% of the viewport) as there.
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const [scrollNav, setScrollNav] = useState({ up: false, down: false });

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;

    const update = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      const maxScroll = scrollHeight - clientHeight;
      setScrollNav({
        up: scrollTop > 8,
        down: maxScroll > 8 && scrollTop < maxScroll - 8,
      });
    };

    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    const ro = new ResizeObserver(update);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);

    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      ro.disconnect();
    };
  }, [step, initializing, verified]);

  const scrollByHint = useCallback((direction: 1 | -1) => {
    const el = bodyRef.current;
    if (!el) return;
    el.scrollBy({ top: direction * el.clientHeight * 0.85, behavior: 'smooth' });
  }, []);

  // Resolve which steps are already satisfied (shown as "done", not skipped).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await dispatch(fetchPersonalData(authUser?.userId) as any).catch(() => null);
      const vRes: any = await dispatch(fetchBankIdVerificationStatus() as any).catch(() => null);
      if (cancelled) return;
      setVerified(isLoginWithBankId || vRes?.payload?.isIdentityVerified === true || isIdentityVerified === true);
      setInitializing(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the form's method selection in sync whenever the fetched record changes.
  useEffect(() => {
    if (personalData) {
      setBankValue(deriveBankMethodValue(personalData));
    }
  }, [personalData]);

  const goNext = useCallback(() => {
    setStep((s) => (s === 'bank' ? 'email' : s === 'email' ? 'verify' : s === 'verify' ? 'finish' : 'finish'));
  }, []);

  const goBack = useCallback(() => {
    if (step === 'bank') onBack();
    else if (step === 'email') setStep('bank');
    else if (step === 'verify') setStep('email');
    else setStep('verify');
  }, [step, onBack]);

  // Bank saved (Brite widget completion, a verified manual OTP submit) or email
  // OTP verified → re-fetch so personalData (and the derived flags below) reflect it.
  const refreshPersonalData = useCallback(() => {
    if (authUser?.userId) {
      dispatch(fetchPersonalData(authUser.userId) as any);
    }
  }, [dispatch, authUser?.userId]);

  const handleVerified = useCallback(() => setVerified(true), []);

  const generateOTPForBank = useCallback(async () => {
    try {
      const language = localStorage.getItem('i18nextLng') || 'en';
      const result = await apiCall.post('/settings/generate-otp', { type: 3, email: '', lang: language });
      if (result?.status === 200) {
        setOtpCounter(300);
        setOtpField(new Array(4).fill(''));
        setOtpModalOpen(true);
        toast.success(String(t('OTP_SENT_TO_EMAIL') || 'OTP sent to your email'));
      }
    } catch (error: any) {
      toast.error(String(t(error?.response?.data?.message) || 'Failed to generate OTP'));
    }
  }, [t]);

  // Validates then kicks off the OTP flow — same shape as profile-settings.page.tsx's
  // handleBankSaveChanges. Brite never calls this; it completes via the widget.
  const handleBankSaveChanges = useCallback(() => {
    const errors: BankFieldErrors = {};
    if (bankValue.method === 'other-swedish') {
      if (!bankValue.otherBankName?.trim()) errors.otherBankName = String(t('PLEASE_ENTER_BANK_NAME') || 'Please enter bank name');
      if (!isValidClearingNumber(bankValue.clearingNumber)) errors.clearingNumber = String(t('INVALID_CLEARING_FORMAT') || 'Invalid clearing number format');
      if (!isValidAccountNumber(bankValue.accountNumber)) errors.accountNumber = String(t('INVALID_ACCOUNT_FORMAT') || 'Invalid account number format');
    } else if (bankValue.method === 'foreign' || bankValue.method === 'revolut') {
      if (!bankValue.ibanNumber?.trim()) errors.ibanNumber = String(t('PLEASE_ENTER_IBAN') || 'Please enter IBAN');
      if (!bankValue.bicNumber?.trim()) errors.bicNumber = String(t('PLEASE_ENTER_BIC') || 'Please enter BIC');
    }
    setBankFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error(Object.values(errors)[0]);
      return;
    }
    generateOTPForBank();
  }, [bankValue, generateOTPForBank, t]);

  const submitBankWithOtp = useCallback(async (otp: string) => {
    try {
      const bankDataPayload = { ...buildManualBankPayload(bankValue), code: otp };
      await dispatch(updateBankData({ userId: authUser?.userId, bankData: bankDataPayload }) as any).unwrap();
      toast.success(String(t('BANK_DETAILS_UPDATED') || 'Your bank details have been added.'));
      setOtpModalOpen(false);
      setOtpField(new Array(4).fill(''));
      refreshPersonalData();
    } catch (err: any) {
      toast.error(String(t(err?.response?.data?.message) || 'Something went wrong'));
    }
  }, [bankValue, authUser?.userId, dispatch, refreshPersonalData, t]);

  const handleChangeOTP = useCallback(async (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (isNaN(Number(value))) return;
    const newOtp = [...otpField];
    newOtp[index] = value.substring(value.length - 1);
    setOtpField(newOtp);
    if (value && index === otpField.length - 1 && newOtp.every((digit) => digit)) {
      await submitBankWithOtp(newOtp.join(''));
    }
  }, [otpField, submitBankWithOtp]);

  const resendBankOtp = useCallback(() => {
    setOtpField(new Array(4).fill(''));
    generateOTPForBank();
  }, [generateOTPForBank]);

  // Same precedence as profile-settings.page.tsx: IBAN + BIC on file always
  // wins; isBriteIdExist only counts as linked when isBriteSessionAccount
  // isn't explicitly false (it can be flipped true some other way, e.g. via
  // admin — profile-settings.slice already resolves a missing flag to true).
  const hasIbanBic = !!personalData?.ibanNumber && !!personalData?.bicNumber;
  const isBriteLinked = !!personalData?.isBriteIdExist && !!personalData?.isBriteSessionAccount && !hasIbanBic;
  const hasAccountClearing = !isBriteLinked && !!personalData?.accountNumber && !!personalData?.clearingNumber;
  const hasManualBankOnFile = hasIbanBic || hasAccountClearing;
  const bankSaved = isBriteLinked || hasManualBankOnFile;
  // bbanNumber only exists nested under customerSalaryInformation (bankAccounts
  // here) — there's no top-level field for it.
  const bbanNumber = personalData?.bankAccounts?.[0]?.bbanNumber || '';

  const emailVerified = !!personalData?.isVerified;

  const completed: ('profile' | 'bank' | 'email' | 'verify' | 'finish')[] = [
    'profile',
    ...(bankSaved ? (['bank'] as const) : []),
    ...(emailVerified ? (['email'] as const) : []),
    ...(verified ? (['verify'] as const) : []),
  ];

  // Overall onboarding progress — profile is already done to reach here, plus
  // Bank Details, Email verification, and ID verification. Drives the progress
  // bar + warning banner.
  const totalSteps = 4;
  const doneSteps = 1 + (bankSaved ? 1 : 0) + (emailVerified ? 1 : 0) + (verified ? 1 : 0);
  const progressPct = Math.round((doneSteps / totalSteps) * 100);
  const allComplete = bankSaved && emailVerified && verified;

  return (
    <div className="onboarding-poststep onboarding-idv">
      {/* Common header — title dropped so only the stepper leads the modal; the
          close button stays (floated right). */}
      <div className="onboarding-poststep__header onboarding-poststep__header--minimal">
        <button className="onboarding-poststep__close" onClick={onComplete} aria-label="Close">
          <AiOutlineClose size={28} />
        </button>
      </div>

      {/* Common stepper — all steps shown, completed ones checked */}
      <OnboardingStepper active={step} completed={completed} />

      {/* A warning (shown on every step until onboarding is finished) explaining
          what stays locked while setup is incomplete. */}
      {!initializing && !allComplete && (
        <div className="onboarding-poststep__warning" role="alert">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"
              stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <p>{t('ONBOARDING_INCOMPLETE_WARNING')}</p>
        </div>
      )}

      {/* Body — swaps per step, inside the same parent modal. Wrapped so the
          swipe hints can float over the scroll region's top/bottom edges. */}
      <div className="onboarding-poststep__scroll-wrap">
        <div className={`onboarding-poststep__hint onboarding-poststep__hint--up${scrollNav.up ? ' is-visible' : ''}`}>
          <button
            type="button"
            className="onboarding-poststep__hint-pill"
            onClick={() => scrollByHint(-1)}
            tabIndex={scrollNav.up ? 0 : -1}
            aria-label={String(t('PARTNER_SWIPE_UP'))}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M4 10l4-4 4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {t('PARTNER_SWIPE_UP')}
          </button>
        </div>

        <div className="onboarding-poststep__body" ref={bodyRef}>
        {initializing ? (
          <div className="onboarding-poststep__loading">
            <span className="onboarding-poststep__spinner" />
          </div>
        ) : step === 'bank' ? (
          isBriteLinked ? (
            <div className="onboarding-poststep__done">
              <div className="onboarding-poststep__done-badge"><DoneIcon /></div>
              <p className="onboarding-poststep__done-title">{t('BANK_DETAILS_ADDED') || 'Bank details added'}</p>
              <p className="onboarding-poststep__done-sub">{t('BANK_DETAILS_ADDED_SUB') || 'Your payout details are saved.'}</p>
              <div className="bank-edit-section">
                <div className="bank-account-row">
                  <div className="bank-detail-item">
                    <label className="detail-label">{t('ACCOUNT_INFORMATION') || 'BBAN Number'}</label>
                    <p className="detail-value detail-value--mono">
                      {bbanNumber ? `******${bbanNumber.slice(-4)}` : '—'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : hasManualBankOnFile ? (
            <div className="onboarding-poststep__done">
              <div className="onboarding-poststep__done-badge"><DoneIcon /></div>
              <p className="onboarding-poststep__done-title">{t('BANK_DETAILS_ADDED') || 'Bank details added'}</p>
              <p className="onboarding-poststep__done-sub">{t('BANK_DETAILS_ADDED_SUB') || 'Your payout details are saved.'}</p>
              <div className="bank-edit-section">
                <div className="bank-account-row">
                  <div className="bank-detail-item">
                    <label className="detail-label">{t('BANK_NAME') || 'Bank Name'}</label>
                    <p className="detail-value">{personalData?.bank || '—'}</p>
                  </div>
                  {hasIbanBic ? (
                    <>
                      <div className="bank-detail-item">
                        <label className="detail-label">{t('IBAN_NUMBER') || 'IBAN Number'}</label>
                        <p className="detail-value detail-value--mono">{personalData?.ibanNumber}</p>
                      </div>
                      <div className="bank-detail-item">
                        <label className="detail-label">{t('BIC_NUMBER') || 'BIC Number'}</label>
                        <p className="detail-value detail-value--mono">{personalData?.bicNumber}</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="bank-detail-item">
                        <label className="detail-label">{t('ACCOUNT_NUMBER') || 'Account Number'}</label>
                        <p className="detail-value detail-value--mono">{personalData?.accountNumber}</p>
                      </div>
                      <div className="bank-detail-item">
                        <label className="detail-label">{t('CLEARING_NUMBER') || 'Clearing Number'}</label>
                        <p className="detail-value detail-value--mono">{personalData?.clearingNumber}</p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="onboarding-bank-form">
              <div className="onboarding-bank-form__selector">
                <BankMethodSelector
                  value={bankValue}
                  onChange={setBankValue}
                  errors={bankFieldErrors}
                  showRevolutMarketing={false}
                  briteContext={{
                    email: personalData?.email || '',
                    firstName: personalData?.firstName || '',
                    lastName: personalData?.lastName || '',
                    dob: personalData?.dateOfBirth === '' ? getCustomerDob(personalData?.ssn) : getCustomerDob(personalData?.dateOfBirth),
                    customerAddress: {
                      city: personalData?.city,
                      street: personalData?.address,
                      postal_code: personalData?.zipCode,
                      country_id: 'se',
                    },
                  }}
                  onBriteCompleted={refreshPersonalData}
                  onBriteError={(message) => toast.error(message)}
                  briteSurface="onboarding-bank"
                />
              </div>

              {/* Trust / reassurance block — fills the space under the short form
                  and builds confidence on the bank step. */}
              <div className="onboarding-bank-form__aside">
                <div className="onboarding-bank-form__trust">
                  <span className="onboarding-bank-form__trust-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M12 2.5 5 5.5v5c0 4.4 3 8.4 7 9.5 4-1.1 7-5.1 7-9.5v-5l-7-3Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                      <path d="M9 12l2 2 4-4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  <div>
                    <p className="onboarding-bank-form__trust-title">{t('ONBOARDING_BANK_SECURE_TITLE')}</p>
                    <p className="onboarding-bank-form__trust-text">{t('ONBOARDING_BANK_SECURE_TEXT')}</p>
                  </div>
                </div>

                <ul className="onboarding-bank-form__features">
                  {[
                    t('ONBOARDING_BANK_FEATURE_ENCRYPTED'),
                    t('ONBOARDING_BANK_FEATURE_PAYOUTS'),
                    t('ONBOARDING_BANK_FEATURE_CONTROL'),
                  ].map((label, i) => (
                    <li key={i} className="onboarding-bank-form__feature">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.14" />
                        <path d="M7.5 12.5l3 3 6-6.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      {label}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )
        ) : step === 'email' ? (
          emailVerified ? (
            <div className="onboarding-poststep__done">
              <div className="onboarding-poststep__done-badge"><DoneIcon /></div>
              <p className="onboarding-poststep__done-title">{t('EMAIL_VERIFIED') || 'Email verified'}</p>
              <p className="onboarding-poststep__done-sub">{t('EMAIL_VERIFIED_SUB') || 'Your email address has been verified.'}</p>
            </div>
          ) : (
            <OnboardingEmailVerification
              email={personalData?.email || ''}
              onSuccess={refreshPersonalData}
            />
          )
        ) : step === 'verify' ? (
          verified ? (
            <div className="onboarding-poststep__done">
              <div className="onboarding-poststep__done-badge"><DoneIcon /></div>
              <p className="onboarding-poststep__done-title">{t('IDENTITY_VERIFIED') || 'Identity verified'}</p>
              {/* Someone who signed in with BankID arrives already verified — they
                  never ran the step, so "your identity has been verified" reads as
                  if something just happened. Name the reason instead. */}
              <p className="onboarding-poststep__done-sub">
                {isLoginWithBankId
                  ? (t('IDENTITY_VERIFIED_SUB_BANKID') || 'Your identity has already been verified because you registered using BankID.')
                  : (t('IDENTITY_VERIFIED_SUB') || 'Your identity has been verified.')}
              </p>
            </div>
          ) : (
            <OnboardingIdentityVerification
              onSuccess={handleVerified}
              onClose={() => { /* stay until verified */ }}
            />
          )
        ) : (
          <div className="onboarding-poststep__finish">
            <div className="onboarding-poststep__finish-illus">
              <span className="onboarding-poststep__finish-glow" aria-hidden="true" />
              <img src={ready_platform} alt="" />
            </div>
            <h3 className="onboarding-poststep__finish-title">{t('ONBOARDING_ALL_SET') || "You're all set!"}</h3>
            <p className="onboarding-poststep__finish-text">{t('PLATFORM_READY_MESSAGE')}</p>
            <ul className="onboarding-poststep__finish-perks">
              {[
                t('ONBOARDING_FINISH_PERK_INVOICE'),
                t('ONBOARDING_FINISH_PERK_PAID'),
                t('ONBOARDING_FINISH_PERK_TRACK'),
              ].map((label, i) => (
                <li key={i} className="onboarding-poststep__finish-perk">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.14" />
                    <path d="M7.5 12.5l3 3 6-6.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {label}
                </li>
              ))}
            </ul>
          </div>
        )}
        </div>

        <div className={`onboarding-poststep__hint onboarding-poststep__hint--down${scrollNav.down ? ' is-visible' : ''}`}>
          <button
            type="button"
            className="onboarding-poststep__hint-pill"
            onClick={() => scrollByHint(1)}
            tabIndex={scrollNav.down ? 0 : -1}
            aria-label={String(t('PARTNER_SWIPE_DOWN'))}
          >
            {t('PARTNER_SWIPE_DOWN')}
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>

      {/* Common footer — Back always; Next when the step is done; Start Tour on finish */}
      {!initializing && (
        <div className="onboarding-poststep__footer">
          <button type="button" className="onboarding-poststep__back" onClick={goBack}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {t('BACK') || 'Back'}
          </button>

          {/* Setup progress — centered between Back and Next */}
          <div className="onboarding-poststep__progress">
            <div className="onboarding-poststep__progress-head">
              <span className="onboarding-poststep__progress-label">
                {allComplete ? (t('ONBOARDING_ALL_SET') || "You're all set!") : t('ONBOARDING_SETUP_PROGRESS')}
              </span>
              <span className="onboarding-poststep__progress-count">
                {doneSteps}/{totalSteps} · {progressPct}%
              </span>
            </div>
            <div className="onboarding-poststep__progress-track">
              <div
                className={`onboarding-poststep__progress-fill${allComplete ? ' is-complete' : ''}`}
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>

          {step === 'finish' ? (
            <button type="button" className="onboarding-poststep__next" onClick={onStartTour}>
              {t('START_TOUR')}
            </button>
          ) : step === 'bank' ? (
            bankSaved ? (
              <button type="button" className="onboarding-poststep__next" onClick={goNext}>
                {t('NEXT')}
              </button>
            ) : bankValue.method === 'brite' ? (
              <span />
            ) : (
              <button
                type="button"
                className="onboarding-poststep__next"
                onClick={handleBankSaveChanges}
              >
                {t('SAVE_AND_NEXT') || 'Save & Next'}
              </button>
            )
          ) : step === 'email' ? (
            /* Next stays DISABLED until the email OTP is actually verified. */
            <button
              type="button"
              className="onboarding-poststep__next"
              onClick={goNext}
              disabled={!emailVerified}
            >
              {t('NEXT')}
            </button>
          ) : step === 'verify' ? (
            /* Next stays DISABLED until ID verification is actually approved —
               documents pending admin review do NOT unlock it. */
            <button
              type="button"
              className="onboarding-poststep__next"
              onClick={goNext}
              disabled={!verified}
            >
              {t('NEXT')}
            </button>
          ) : (
            <span />
          )}
        </div>
      )}

      <BankOTPModal
        OTPModal={otpModalOpen}
        setOTPModal={(v: boolean | number) => setOtpModalOpen(!!v)}
        resendOTP={resendBankOtp}
        OTPField={otpField}
        setOTPField={setOtpField}
        inputRefs={otpInputRefs}
        handleChangeOTP={handleChangeOTP}
        counter={otpCounter}
        setCounter={setOtpCounter}
        verifyEmail={null}
        newEmail={null}
        openInModal={false}
      />
    </div>
  );
};

export default OnboardingPostProfileSteps;
